/**
 * Today's Menu Component
 * Displays the current day's lunch option.
 */

import { useState } from 'react';
import { useTodaysMenu } from '../../hooks/useMenu';
import { ItemOptionsPicker } from './ItemOptionsPicker';

interface TodaysMenuProps {
  onOrder?: (menuItemId: number, menuItemName: string, optionIds?: number[], optionNames?: string[]) => void;
  disabled?: boolean;
}

export function TodaysMenu({ onOrder, disabled = false }: TodaysMenuProps) {
  const { menu, loading, error } = useTodaysMenu();
  const [selectedOptions, setSelectedOptions] = useState<number[]>([]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden h-full flex flex-col">
        <div className="bg-hmh-blue px-4 py-3">
          <h3 className="text-lg font-bold text-white">Lunch</h3>
        </div>
        <div className="p-6 flex-grow">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-full"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden h-full flex flex-col">
        <div className="bg-hmh-blue px-4 py-3">
          <h3 className="text-lg font-bold text-white">Lunch</h3>
        </div>
        <div className="p-6 flex-grow">
          <p className="text-gray-500 text-sm">Unable to load menu</p>
        </div>
      </div>
    );
  }

  const regularLunch = menu?.regular_lunch;
  const dailySpecial = menu?.daily_special;
  const displayItem = dailySpecial?.replaces_regular ? dailySpecial : regularLunch;

  const itemOptions = displayItem?.item_options ?? null;
  const needsPick = !!itemOptions && itemOptions.options.length > 0;
  const pickMissing = needsPick && selectedOptions.length === 0;

  const handleOrderClick = () => {
    if (disabled) return;
    if (displayItem && displayItem.id && onOrder) {
      const names = itemOptions
        ? itemOptions.options.filter((o) => selectedOptions.includes(o.id)).map((o) => o.name)
        : [];
      onOrder(displayItem.id, displayItem.name,
              needsPick ? selectedOptions : undefined,
              needsPick ? names : undefined);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow h-full flex flex-col">
      {/* Blue header banner */}
      <div className="bg-hmh-blue px-4 py-3 flex items-center justify-between">
        <h3 className="text-lg font-bold text-white">Lunch</h3>
        <span className="text-xs text-white/80">Week {menu?.week_number}</span>
      </div>

      {/* Card content with padding */}
      <div className="p-6 flex-grow flex flex-col">
        <div className="flex-grow">
          {displayItem ? (
            <>
              <p className="text-xl font-bold text-gray-900 mb-2">{displayItem.name}</p>
              {displayItem.description && (
                <p className="text-gray-500 text-base mb-4">{displayItem.description}</p>
              )}
              {needsPick && itemOptions && (
                <ItemOptionsPicker
                  itemOptions={itemOptions}
                  selected={selectedOptions}
                  onChange={setSelectedOptions}
                  disabled={disabled}
                />
              )}
            </>
          ) : (
            <p className="text-gray-500 text-base mb-4">No menu available</p>
          )}
        </div>

        <button
          onClick={handleOrderClick}
          disabled={disabled || !displayItem?.id || pickMissing}
          className={`w-full py-3 px-4 rounded-lg text-base font-medium transition-colors ${
            disabled || !displayItem?.id || pickMissing
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-hmh-blue text-white hover:bg-hmh-dark'
          }`}
        >
          {pickMissing ? 'Select an option above' : 'Add to Order'}
        </button>
      </div>
    </div>
  );
}
